import { getDb, type OutboxOp } from './dexie';
import type { DiaClient } from './client';
import type { SyncTable, Uuid } from './types';

/**
 * The outbox (Section 11).
 *
 * Every write in both apps goes here first. The screen shows the local write
 * immediately and a small "guardado, pendiente de enviar" state until the
 * island confirms it. The drain runs in order, retries with backoff, and can
 * be run twice at once without sending anything twice.
 *
 * Duplicates are impossible rather than unlikely: each op carries a client
 * generated UUID which is also the row's client_id, and client_id is unique in
 * every table a phone can write to. An upsert on that column turns a retry
 * after a timeout, where the row actually landed, into a no-op.
 */

export function newId(): Uuid {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  // Only reachable on an old browser without crypto. Good enough to be unique
  // on one device, which is all client_id has to be.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** 2 s, 4 s, 8 s, up to 5 minutes, with a little jitter so a crew of six does not stampede. */
export function backoffMs(attempts: number, random = Math.random): number {
  const base = Math.min(5 * 60_000, 2_000 * 2 ** Math.max(0, attempts - 1));
  return Math.round(base * (0.85 + random() * 0.3));
}

export type EnqueueInput = {
  table: SyncTable;
  kind?: 'insert' | 'update';
  payload: Record<string, unknown>;
  match?: Record<string, unknown>;
  /** Supply one to reuse an id you already put in the row (a photo's id). */
  id?: Uuid;
};

/**
 * Queues one write. Returns the client id, which the caller normally also
 * writes into its local cache row so the optimistic view and the server row
 * are the same thing.
 */
export async function enqueue(input: EnqueueInput, now = Date.now()): Promise<OutboxOp> {
  const db = getDb();
  const id = input.id ?? newId();
  const kind = input.kind ?? 'insert';

  const op: OutboxOp = {
    id,
    table: input.table,
    kind,
    // An insert carries its own client_id so the server can dedupe it.
    payload: kind === 'insert' ? { client_id: id, ...input.payload } : { ...input.payload },
    match: input.match,
    seq: await nextSeq(),
    createdAt: now,
    attempts: 0,
    nextAttemptAt: now,
    status: 'pending',
  };

  await db.outbox.put(op);
  return op;
}

async function nextSeq(): Promise<number> {
  const db = getDb();
  const last = await db.outbox.orderBy('seq').last();
  const stored = (await db.meta.get('outbox.seq'))?.value as number | undefined;
  const next = Math.max(last?.seq ?? 0, stored ?? 0) + 1;
  await db.meta.put({ key: 'outbox.seq', value: next });
  return next;
}

/**
 * Clears the waiting time on everything queued.
 *
 * Called when the phone tells us the network is back, or when a person pulls
 * to refresh. A queue that failed six times in a jungle corner would otherwise
 * sit on a five minute backoff while the crew member stands there with four
 * bars, which is exactly the moment the work should go.
 */
export async function resetBackoff(now = Date.now()): Promise<number> {
  const store = getDb();
  // A parked op is not waiting for anything. It waits for a person.
  const waiting = await store.outbox
    .filter((op) => op.status !== 'dead' && op.nextAttemptAt > now)
    .toArray();
  await Promise.all(waiting.map((op) => store.outbox.update(op.id, { nextAttemptAt: now })));
  return waiting.length;
}

/**
 * When the queue next has something to try, or null when it has nothing.
 *
 * The engine sleeps until this moment instead of waiting for the 60 second
 * interval (defect R3). Without it a two second backoff cost a full minute, six
 * attempts took about four minutes rather than the two the retry ladder is
 * built for, and everything queued behind the stuck write waited that long too.
 */
export async function nextRetryAt(): Promise<number | null> {
  const queue = await getDb().outbox.filter((op) => op.status !== 'dead').toArray();
  if (queue.length === 0) return null;
  return queue.reduce((soonest, op) => Math.min(soonest, op.nextAttemptAt), Infinity);
}

/** Writes still on their way. A parked op is not one of them. */
export async function pendingCount(): Promise<number> {
  return getDb().outbox.filter((op) => op.status !== 'dead').count();
}

export async function pendingOps(): Promise<OutboxOp[]> {
  return (await getDb().outbox.orderBy('seq').toArray()).filter((op) => op.status !== 'dead');
}

/** Everything in the parking bay, oldest first. What the manager is shown. */
export async function deadOps(): Promise<OutboxOp[]> {
  return (await getDb().outbox.orderBy('seq').toArray()).filter((op) => op.status === 'dead');
}

export async function deadCount(): Promise<number> {
  return getDb().outbox.filter((op) => op.status === 'dead').count();
}

/** Put one parked op back in the queue. It keeps its place in the order. */
export async function retryDeadOp(id: Uuid, now = Date.now()): Promise<boolean> {
  const db = getDb();
  const op = await db.outbox.get(id);
  if (!op || op.status !== 'dead') return false;
  await db.outbox.update(id, {
    status: 'pending',
    attempts: 0,
    nextAttemptAt: now,
    deadAt: undefined,
  });
  return true;
}

export async function retryAllDead(now = Date.now()): Promise<number> {
  const parked = await deadOps();
  for (const op of parked) await retryDeadOp(op.id, now);
  return parked.length;
}

/**
 * Throw one away, on purpose, with somebody watching.
 *
 * This is the only path in the whole app that loses a write, which is why it is
 * a separate verb and not something the drain ever does by itself.
 */
export async function discardDeadOp(id: Uuid): Promise<boolean> {
  const db = getDb();
  const op = await db.outbox.get(id);
  if (!op || op.status !== 'dead') return false;
  await db.outbox.delete(id);
  return true;
}

export async function discardAllDead(): Promise<number> {
  const parked = await deadOps();
  const store = getDb();
  for (const op of parked) await store.outbox.delete(op.id);
  return parked.length;
}

/**
 * Attempts on a server refusal before the op is parked (defect D5).
 *
 * Six, with the usual backoff, is about two minutes of trying: 2, 4, 8, 16 and
 * 32 seconds between the attempts, a little over a minute of waiting plus the
 * attempts themselves. Long enough that a policy that was briefly wrong, or a
 * row whose parent had not landed yet, gets its chance; short enough that a
 * crew member is not cleaning a house behind a queue that will never move.
 *
 * The engine has to actually run on that ladder for this to be true, which is
 * what `nextRetryAt` above is for.
 */
export const MAX_SEND_ATTEMPTS = 6;

/**
 * Is this worth waiting out, or has the island actually said no?
 *
 * Waiting out: no network, a timeout, a dropped connection, a gateway having a
 * moment. Every one of those is normal here and the queue must keep its order
 * through them, so the drain stops and tries the same op again later.
 *
 * Said no: a column that does not exist, a policy that refuses, a constraint
 * that fails, a body PostgREST cannot parse. Retrying those forever is what
 * turned defect D7 into a dead queue.
 */
export function isTransientError(message: string): boolean {
  const text = message.toLowerCase();
  const transient = [
    'network',
    'fetch',
    'timeout',
    'timed out',
    'abort',
    'connection',
    'econn',
    'socket',
    'offline',
    'unreachable',
    'temporarily',
    'try again',
    'service unavailable',
    'bad gateway',
    'gateway timeout',
    'too many requests',
    'load failed',
  ];
  return transient.some((needle) => text.includes(needle));
}

export type DrainResult = {
  sent: number;
  failed: number;
  remaining: number;
  /** True when the drain stopped early because an op could not be sent. */
  stalled: boolean;
  /** Ops parked this pass because the island will never accept them. */
  parked: number;
};

/**
 * Puts ops left on `sending` back to `pending` (the row half of defect D3).
 *
 * The drain already picked these up again, because it reads every row rather
 * than filtering on status, but the status itself was a lie: a queue full of
 * `sending` rows with nothing sending is not a state anybody should have to
 * interpret. A fresh start clears it in one statement.
 */
export async function recoverStaleOps(now = Date.now()): Promise<number> {
  const store = getDb();
  const stuck = await store.outbox.where('status').equals('sending').toArray();
  await Promise.all(
    stuck.map((op) => store.outbox.update(op.id, { status: 'pending', nextAttemptAt: now })),
  );
  return stuck.length;
}

/**
 * Sends everything whose backoff has elapsed, oldest first.
 *
 * It stops at the first failure the island might yet recover from. Stopping
 * matters: a run has to exist before its run_items can reference it, so
 * draining past a stuck op would send the child before the parent and turn one
 * retry into a permanent foreign key error.
 *
 * It does not stop forever, which was defect D5. An op the island refuses for a
 * reason waiting cannot fix is retried MAX_SEND_ATTEMPTS times and then parked
 * as `dead`, and the drain carries on with the rest of the queue. The parked op
 * is counted separately, shown, and can be retried or discarded by hand. A
 * transient failure is never parked, however many times it happens: no signal
 * for a week is still no signal, not a refusal.
 */
export async function drainOutbox(
  client: DiaClient,
  now = Date.now(),
  backoff = backoffMs,
): Promise<DrainResult> {
  const store = getDb();
  const queue = (await store.outbox.orderBy('seq').toArray()).filter((op) => op.status !== 'dead');

  let sent = 0;
  let failed = 0;
  let parked = 0;
  let stalled = false;

  for (const op of queue) {
    if (op.nextAttemptAt > now) {
      // Still cooling off. Everything behind it waits too, to keep the order.
      stalled = true;
      break;
    }

    await store.outbox.update(op.id, { status: 'sending' });

    try {
      await sendOp(client, op);
      await store.outbox.delete(op.id);
      sent += 1;
      continue;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const attempts = op.attempts + 1;
      failed += 1;

      if (!isTransientError(message) && attempts >= MAX_SEND_ATTEMPTS) {
        await store.outbox.update(op.id, {
          status: 'dead',
          attempts,
          deadAt: now,
          lastError: message,
        });
        parked += 1;
        // The island has answered. Nothing behind this op is waiting on it, so
        // the rest of the queue goes.
        continue;
      }

      await store.outbox.update(op.id, {
        status: 'failed',
        attempts,
        nextAttemptAt: now + backoff(attempts),
        lastError: message,
      });
      stalled = true;
      break;
    }
  }

  return {
    sent,
    failed,
    parked,
    remaining: await pendingCount(),
    stalled,
  };
}

async function sendOp(client: DiaClient, op: OutboxOp): Promise<void> {
  if (op.kind === 'insert') {
    // onConflict client_id plus ignoreDuplicates: a row that already landed is
    // left exactly as it is, so a retry can never overwrite a reviewed run or
    // resurrect an edited note.
    const { error } = await client
      .from(op.table)
      // The payload is checked by the database, not by the type system: these
      // rows are assembled from user input on a phone.
      .upsert(op.payload as never, { onConflict: 'client_id', ignoreDuplicates: true });
    if (error) throw new Error(error.message);
    return;
  }

  if (!op.match || Object.keys(op.match).length === 0) {
    throw new Error(`update op ${op.id} has no match, refusing to touch the whole table`);
  }

  // The column names come from the caller's match object, so the builder is
  // used through a loose shape rather than fought with generics.
  type LooseFilter = PromiseLike<{ error: { message: string } | null }> & {
    eq(column: string, value: unknown): LooseFilter;
  };

  let query = client.from(op.table).update(op.payload as never) as unknown as LooseFilter;
  for (const [column, value] of Object.entries(op.match)) {
    query = query.eq(column, value);
  }
  const { error } = await query;
  if (error) throw new Error(error.message);
}
